import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';
import { Company } from '../company.model';
import { CompanyGroup, CompanyGroupMembershipAuditEvent, CompanyGroupMembershipInvitation } from '../company-group.model';
import { CompanyService } from '../company.service';
import { TenantAccessService } from '../tenant-access.service';
import { CompanyAccess, CompanyGroupAccess } from '../tenant-access.model';
import { CustomerService } from '../../customer/customer.service';
import { Customer } from '../../customer/customer.model';
import { Role } from '../../customer/customer-role.model';
import { SessionStorageService } from '../../services/session-storage.service';
import { CompanyGroupTenantAccessComponent } from '../company-group-tenant-access/company-group-tenant-access.component';
import { SisterCompanyAccessComponent } from '../sister-company-access/sister-company-access.component';

@Component({
    selector: 'app-company-group-access',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        MultiSelectModule,
        SelectModule,
        TableModule,
        TabsModule,
        TagModule,
        ToastModule,
        TooltipModule,
        CompanyGroupTenantAccessComponent,
        SisterCompanyAccessComponent
    ],
    providers: [MessageService],
    template: `
        <p-toast></p-toast>
        <div class="group-access-shell">
            <div class="group-access-toolbar">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select
                            inputId="companyGroup"
                            [options]="groupOptions"
                            [(ngModel)]="selectedGroupId"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            styleClass="w-full"
                            (onChange)="onGroupSelected()"></p-select>
                        <label for="companyGroup">Company group</label>
                    </p-iftalabel>
                </p-inputGroup>
                <div class="group-toolbar-actions">
                    <p-button
                        type="button"
                        icon="pi pi-plus"
                        label="New Group"
                        severity="secondary"
                        (click)="openNewGroupDialog()"></p-button>
                    <p-button
                        type="button"
                        icon="pi pi-link"
                        label="Create Secure Invitation"
                        [disabled]="!selectedGroupId"
                        (click)="openSecureInvitationDialog()"></p-button>
                </div>
            </div>

            <p-tabs [value]="activeTab">
                <p-tablist>
                    <p-tab [value]="0"><span class="tab-label"><i class="pi pi-building"></i> Child Companies</span></p-tab>
                    <p-tab [value]="1"><span class="tab-label"><i class="pi pi-sitemap"></i> Group Users</span></p-tab>
                    <p-tab [value]="2"><span class="tab-label"><i class="pi pi-shield"></i> Secure Invitations</span></p-tab>
                    <p-tab [value]="3"><span class="tab-label"><i class="pi pi-users"></i> Your Tenant Users</span></p-tab>
                    <p-tab [value]="4"><span class="tab-label"><i class="pi pi-share-alt"></i> Sister-company Access</span></p-tab>
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel [value]="0">
                        <p-table [value]="groupCompanies" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '42rem' }">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>ID</th>
                                    <th>Company</th>
                                    <th>Country</th>
                                    <th class="text-right">Actions</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr>
                                    <td>{{ row.id }}</td>
                                    <td><strong>{{ row.name }}</strong></td>
                                    <td>{{ row.countryId || 'N/A' }}</td>
                                    <td class="text-right">
                                        <p-tag *ngIf="isOwnerCompany(row)" value="Owner" severity="info"></p-tag>
                                        <button
                                            *ngIf="!isOwnerCompany(row)"
                                            pButton
                                            type="button"
                                            icon="pi pi-trash"
                                            pTooltip="Immediately revoke membership"
                                            class="p-button-rounded p-button-text p-button-danger"
                                            (click)="revokeCompanyFromGroup(row)"
                                            [disabled]="!selectedGroupId"></button>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="4" class="text-center p-4">No registered child companies in this group.</td></tr>
                            </ng-template>
                        </p-table>
                        <h4 class="audit-heading">Immutable activity history</h4>
                        <p-table [value]="membershipAuditEvents" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '48rem' }">
                            <ng-template pTemplate="header">
                                <tr><th>When</th><th>Event</th><th>Company</th><th>Detail</th></tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr>
                                    <td>{{ row.eventAt | date:'medium' }}</td>
                                    <td><p-tag [value]="row.eventType" [severity]="invitationSeverity(row.eventType === 'INVITED' ? 'PENDING' : row.eventType)"></p-tag></td>
                                    <td>{{ row.targetCompanyName || row.targetCompanyId }}</td>
                                    <td>{{ row.detail || '—' }}</td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="4" class="text-center p-4">No membership activity has been recorded.</td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <p-tabpanel [value]="1">
                        <div class="assignment-row">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select
                                        inputId="groupUser"
                                        [options]="customerOptions"
                                        [(ngModel)]="groupCustomerId"
                                        optionLabel="label"
                                        optionValue="value"
                                        appendTo="body"
                                        styleClass="w-full"></p-select>
                                    <label for="groupUser">Group user</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-multiSelect
                                        inputId="groupRoles"
                                        [options]="groupRoleOptions"
                                        [(ngModel)]="groupRoleIds"
                                        optionLabel="label"
                                        optionValue="value"
                                        appendTo="body"
                                        styleClass="w-full"></p-multiSelect>
                                    <label for="groupRoles">Group roles</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <div class="assignment-actions">
                                <p-button type="button" icon="pi pi-save" [label]="editingGroupAccess ? 'Update Group Access' : 'Save Group Access'" (click)="saveGroupAccess()" [disabled]="!canSaveGroupAccess()"></p-button>
                                <p-button *ngIf="editingGroupAccess" type="button" icon="pi pi-times" label="Cancel" severity="secondary" [outlined]="true" (click)="resetGroupAccessForm()"></p-button>
                            </div>
                        </div>
                        <p-table [value]="groupUsers" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '58rem' }">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Access ID</th>
                                    <th>User</th>
                                    <th>Group</th>
                                    <th>Status</th>
                                    <th>Group Roles</th>
                                    <th class="text-right">Actions</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr>
                                    <td>{{ row.accessId }}</td>
                                    <td>
                                        <div class="user-cell">
                                            <strong>{{ accessUserName(row) }}</strong>
                                            <span>{{ accessUserDetail(row) }}</span>
                                        </div>
                                    </td>
                                    <td>{{ row.companyGroupName }}</td>
                                    <td><p-tag [value]="row.active ? 'Active' : 'Inactive'" [severity]="row.active ? 'success' : 'secondary'"></p-tag></td>
                                    <td><p-tag *ngFor="let role of row.roles" [value]="role.roleName" severity="info"></p-tag></td>
                                    <td class="text-right">
                                        <button
                                            pButton
                                            type="button"
                                            icon="pi pi-pencil"
                                            pTooltip="Edit group access"
                                            class="p-button-rounded p-button-text p-button-secondary"
                                            (click)="editGroupAccess(row)"></button>
                                        <button
                                            pButton
                                            type="button"
                                            icon="pi pi-trash"
                                            pTooltip="Delete group access"
                                            class="p-button-rounded p-button-text p-button-danger"
                                            (click)="deleteGroupAccess(row)"></button>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="6" class="text-center p-4">No group users assigned.</td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <p-tabpanel [value]="2">
                        <div class="security-note">
                            <i class="pi pi-shield"></i>
                            Invitations are bound to a specific company. The recipient must sign in as an authorized company administrator and reauthenticate before membership is activated. Membership never shares data or roles automatically.
                        </div>
                        <p-table [value]="invitations" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '48rem' }">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Company ID</th>
                                    <th>Company</th>
                                    <th>Status</th>
                                    <th>Expires</th>
                                    <th class="text-right">Actions</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr>
                                    <td>{{ row.targetCompanyId }}</td>
                                    <td>{{ row.targetCompanyName || 'Protected tenant' }}</td>
                                    <td><p-tag [value]="row.status" [severity]="invitationSeverity(row.status)"></p-tag></td>
                                    <td>{{ row.expiresAt | date:'medium' }}</td>
                                    <td class="text-right">
                                        <button *ngIf="row.status === 'PENDING'" pButton type="button" icon="pi pi-times"
                                            pTooltip="Cancel invitation" class="p-button-rounded p-button-text p-button-danger"
                                            (click)="cancelInvitation(row)"></button>
                                    </td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="5" class="text-center p-4">No secure invitations have been created.</td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <p-tabpanel [value]="3">
                        <app-company-group-tenant-access [company]="company"></app-company-group-tenant-access>
                    </p-tabpanel>
                    <p-tabpanel [value]="4">
                        <app-sister-company-access></app-sister-company-access>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>

            <p-dialog
                header="New Company Group"
                [(visible)]="showGroupDialog"
                [modal]="true"
                [style]="{ width: 'min(92vw, 520px)' }">
                <div class="group-form">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-sitemap"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input pInputText id="groupName" [(ngModel)]="groupDraft.name" class="w-full" />
                            <label for="groupName">Group name</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input pInputText id="groupCode" [(ngModel)]="groupDraft.code" class="w-full" />
                            <label for="groupCode">Group code</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-align-left"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input pInputText id="groupDescription" [(ngModel)]="groupDraft.description" class="w-full" />
                            <label for="groupDescription">Description</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
                <ng-template pTemplate="footer">
                    <p-button type="button" label="Cancel" severity="secondary" [text]="true" (click)="showGroupDialog = false"></p-button>
                    <p-button
                        type="button"
                        label="Create"
                        icon="pi pi-save"
                        [loading]="isSavingGroup"
                        [disabled]="!canSaveNewGroup() || isSavingGroup"
                        (click)="saveNewGroup()"></p-button>
                </ng-template>
            </p-dialog>

            <p-dialog
                header="Create Secure Company Invitation"
                [(visible)]="showSecureInvitationDialog"
                [modal]="true"
                [style]="{ width: 'min(92vw, 520px)' }">
                <div class="group-form">
                    <p class="dialog-hint">Enter the known Company ID supplied by the recipient. No company directory is searched or exposed.</p>
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input pInputText id="targetCompanyId" inputmode="numeric" [(ngModel)]="invitationTargetCompanyId" class="w-full" />
                            <label for="targetCompanyId">Target company ID</label>
                        </p-iftalabel>
                    </p-inputGroup>
                    <div class="security-note"><i class="pi pi-lock"></i> The link expires in 72 hours, is single-use, and works only for an authorized administrator of that exact company.</div>
                </div>
                <ng-template pTemplate="footer">
                    <p-button type="button" label="Cancel" severity="secondary" [text]="true" (click)="showSecureInvitationDialog = false"></p-button>
                    <p-button type="button" label="Create link" icon="pi pi-link" [loading]="isCreatingInvitation"
                        [disabled]="!validInvitationTarget() || isCreatingInvitation" (click)="createSecureInvitation()"></p-button>
                </ng-template>
            </p-dialog>

            <p-dialog
                header="Share Secure Invitation"
                [(visible)]="showInvitationLinkDialog"
                [modal]="true"
                [closable]="true"
                [style]="{ width: 'min(92vw, 640px)' }">
                <div class="group-form">
                    <div class="security-note"><i class="pi pi-exclamation-triangle"></i> Copy this link now and send it to MAS through your trusted channel. It is shown only once and does not grant access by itself.</div>
                    <textarea pInputText class="w-full invitation-link" [value]="createdInvitationLink" readonly rows="4"></textarea>
                </div>
                <ng-template pTemplate="footer">
                    <p-button type="button" label="Copy Link" icon="pi pi-copy" (click)="copyInvitationLink()"></p-button>
                    <p-button type="button" label="Done" severity="secondary" (click)="closeInvitationLinkDialog()"></p-button>
                </ng-template>
            </p-dialog>
        </div>
    `,
    styles: [`
        .group-access-shell {
            display: grid;
            gap: 1rem;
        }

        .group-access-toolbar {
            display: grid;
            grid-template-columns: minmax(280px, 1fr) auto;
            gap: .75rem;
            align-items: center;
        }

        .group-toolbar-actions {
            display: inline-flex;
            align-items: center;
            gap: .6rem;
            justify-content: flex-end;
        }

        .assignment-row {
            display: grid;
            grid-template-columns: minmax(180px, 1fr) minmax(220px, 1.2fr) auto;
            gap: .75rem;
            align-items: center;
            margin-bottom: 1rem;
        }

        p-tabpanel:nth-child(3) .assignment-row {
            grid-template-columns: minmax(170px, 1fr) minmax(170px, 1fr) minmax(220px, 1.2fr) auto;
        }

        .group-form {
            display: grid;
            gap: 1rem;
        }

        .assignment-actions {
            display: inline-flex;
            align-items: center;
            gap: .5rem;
            justify-content: flex-end;
            flex-wrap: wrap;
        }

        .user-cell {
            display: grid;
            gap: .15rem;
            line-height: 1.25;
        }

        .user-cell span {
            color: var(--text-color-secondary);
            font-size: .82rem;
        }

        .security-note {
            display: flex;
            gap: .55rem;
            align-items: flex-start;
            padding: .8rem;
            margin-bottom: 1rem;
            border-radius: .5rem;
            background: color-mix(in srgb, var(--primary-color) 8%, transparent);
            color: var(--text-color-secondary);
            line-height: 1.4;
        }

        .dialog-hint { margin: 0; color: var(--text-color-secondary); }
        .invitation-link { resize: none; font-family: monospace; }
        .audit-heading { margin: 1.5rem 0 .7rem; }

        :host ::ng-deep p-iftalabel,
        :host ::ng-deep .p-iftalabel,
        :host ::ng-deep .p-select,
        :host ::ng-deep .p-multiselect {
            width: 100%;
        }

        @media (max-width: 900px) {
            .group-access-toolbar {
                grid-template-columns: 1fr;
            }

            .group-toolbar-actions {
                justify-content: flex-start;
                flex-wrap: wrap;
            }

            .assignment-row,
            p-tabpanel:nth-child(3) .assignment-row {
                grid-template-columns: 1fr;
            }
        }
    `]
})
export class CompanyGroupAccessComponent implements OnInit, OnChanges {
    @Input() company?: Company;

    activeTab = 0;
    groups: CompanyGroup[] = [];
    groupCompanies: Company[] = [];
    groupUsers: CompanyGroupAccess[] = [];
    invitations: CompanyGroupMembershipInvitation[] = [];
    membershipAuditEvents: CompanyGroupMembershipAuditEvent[] = [];
    companyUsers: CompanyAccess[] = [];
    customers: Customer[] = [];
    roles: Role[] = [];

    selectedGroupId?: number;
    childCompanyId?: number;
    groupCustomerId?: number;
    companyCustomerId?: number;
    groupRoleIds: number[] = [];
    companyRoleIds: number[] = [];
    editingGroupAccess?: CompanyGroupAccess;
    showGroupDialog = false;
    isSavingGroup = false;
    groupDraft: Partial<CompanyGroup> = this.createGroupDraft();
    showSecureInvitationDialog = false;
    showInvitationLinkDialog = false;
    invitationTargetCompanyId?: number;
    createdInvitationLink = '';
    isCreatingInvitation = false;

    constructor(
        private companyService: CompanyService,
        private tenantAccessService: TenantAccessService,
        private customerService: CustomerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadGroups();
        this.customerService.getAllRoles('ANY').subscribe(roles => {
            this.roles = (roles ?? []).filter(role => !!role.roleId);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['company'] && this.company?.id) {
            this.loadGroups();
            this.loadCustomers();
        }
    }

    get groupOptions() {
        return this.groups.map(group => ({ label: group.name, value: group.id }));
    }

    get childCompanyOptions() {
        return this.groupCompanies.map(company => ({ label: company.name, value: company.id }));
    }

    get customerOptions() {
        return this.customers.map(customer => ({
            label: `${customer.name || 'User'}${customer.emailAddress ? ' - ' + customer.emailAddress : ''}`,
            value: customer.id
        }));
    }

    get companyRoleOptions() {
        return this.roles
            .filter(role => this.isCompanyRole(role))
            .map(role => ({ label: role.roleName, value: role.roleId }));
    }

    get groupRoleOptions() {
        const scopedGroupRoles = this.roles
            .filter(role => this.isCompanyGroupRole(role))
            .map(role => ({ label: role.roleName, value: role.roleId }));
        if (scopedGroupRoles.length) {
            return scopedGroupRoles;
        }
        return this.roles
            .filter(role => this.isCompanyRole(role) && !this.hasPortalPermission(role))
            .map(role => ({ label: `Group ${role.roleName}`, value: role.roleId }));
    }

    onGroupSelected(): void {
        this.loadGroupDetails();
        this.loadGroupUsers();
        this.loadInvitations();
        this.loadMembershipAuditEvents();
    }

    openNewGroupDialog(): void {
        this.groupDraft = this.createGroupDraft();
        this.showGroupDialog = true;
    }

    canSaveNewGroup(): boolean {
        return !!this.groupDraft.name?.trim() && !!this.groupDraft.code?.trim();
    }

    saveNewGroup(): void {
        if (!this.canSaveNewGroup() || this.isSavingGroup) {
            return;
        }

        this.isSavingGroup = true;
        this.companyService.saveCompanyGroup({
            ...this.groupDraft,
            name: this.groupDraft.name?.trim(),
            code: this.groupDraft.code?.trim(),
            description: this.groupDraft.description?.trim(),
            countryId: this.company?.countryId,
            active: true
        }).subscribe({
            next: group => {
                this.groups = [group, ...this.groups.filter(item => item.id !== group.id)];
                this.selectedGroupId = group.id;
                this.showGroupDialog = false;
                this.isSavingGroup = false;
                this.messageService.add({ severity: 'success', summary: 'Created', detail: 'Company group created.' });
                this.onGroupSelected();
            },
            error: err => {
                this.isSavingGroup = false;
                this.messageService.add({ severity: 'error', summary: 'Create Failed', detail: err?.error?.message || 'Could not create company group.' });
            }
        });
    }

    openSecureInvitationDialog(): void {
        this.invitationTargetCompanyId = undefined;
        this.showSecureInvitationDialog = true;
    }

    validInvitationTarget(): boolean {
        return Number.isSafeInteger(Number(this.invitationTargetCompanyId)) && Number(this.invitationTargetCompanyId) > 0;
    }

    createSecureInvitation(): void {
        if (!this.selectedGroupId || !this.validInvitationTarget() || this.isCreatingInvitation) {
            return;
        }
        this.isCreatingInvitation = true;
        this.companyService.createCompanyGroupMembershipInvitation(
            this.selectedGroupId,
            Number(this.invitationTargetCompanyId)
        ).subscribe({
            next: created => {
                this.isCreatingInvitation = false;
                this.showSecureInvitationDialog = false;
                this.createdInvitationLink = created.acceptanceUrl;
                this.showInvitationLinkDialog = true;
                this.loadInvitations();
                this.loadMembershipAuditEvents();
            },
            error: err => {
                this.isCreatingInvitation = false;
                this.messageService.add({ severity: 'error', summary: 'Invitation not created', detail: err?.error?.message || 'The secure invitation could not be created.' });
            }
        });
    }

    closeInvitationLinkDialog(): void {
        this.showInvitationLinkDialog = false;
        this.createdInvitationLink = '';
    }

    copyInvitationLink(): void {
        if (!this.createdInvitationLink) {
            return;
        }
        if (!navigator.clipboard) {
            this.messageService.add({ severity: 'warn', summary: 'Copy unavailable', detail: 'Select and copy the link manually.' });
            return;
        }
        navigator.clipboard.writeText(this.createdInvitationLink).then(
            () => this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Secure invitation link copied.' }),
            () => this.messageService.add({ severity: 'warn', summary: 'Copy unavailable', detail: 'Select and copy the link manually.' })
        );
    }

    cancelInvitation(invitation: CompanyGroupMembershipInvitation): void {
        if (!invitation?.id || !window.confirm('Cancel this secure invitation? The link will stop working immediately.')) {
            return;
        }
        this.companyService.cancelCompanyGroupMembershipInvitation(invitation.id).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'The secure invitation no longer works.' });
                this.loadInvitations();
                this.loadMembershipAuditEvents();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Cancel failed', detail: err?.error?.message || 'The invitation could not be cancelled.' })
        });
    }

    revokeCompanyFromGroup(company: Company): void {
        if (!this.selectedGroupId || !company?.id || this.isOwnerCompany(company)) {
            return;
        }
        const reason = window.prompt(`Revoke ${company.name || 'this company'} from the group? An optional reason will be recorded in the audit trail.`);
        if (reason === null || !window.confirm(`Immediately revoke ${company.name || 'this company'}? Their group access will stop at once.`)) {
            return;
        }
        this.companyService.revokeCompanyGroupMembership(this.selectedGroupId, company.id, reason || undefined).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Relationship revoked', detail: 'Membership and group access were removed immediately.' });
                this.loadGroupDetails();
                this.loadGroupUsers();
                this.loadMembershipAuditEvents();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Revocation failed', detail: err?.error?.message || 'The relationship could not be revoked.' })
        });
    }

    isOwnerCompany(company: Company): boolean {
        return !!company?.id && company.id === this.currentGroup?.ownerCompanyId;
    }

    invitationSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case 'ACCEPTED': return 'success';
            case 'PENDING': return 'warn';
            case 'DECLINED':
            case 'CANCELLED': return 'secondary';
            case 'EXPIRED': return 'danger';
            default: return 'info';
        }
    }

    canSaveGroupAccess(): boolean {
        return !!this.selectedGroupId && !!this.groupCustomerId && this.groupRoleIds.length > 0;
    }

    canSaveCompanyAccess(): boolean {
        return !!this.childCompanyId && !!this.companyCustomerId && this.companyRoleIds.length > 0;
    }

    saveGroupAccess(): void {
        if (!this.canSaveGroupAccess()) {
            return;
        }
        this.tenantAccessService.saveCompanyGroupUser(this.selectedGroupId!, {
            customerId: this.groupCustomerId!,
            roleIds: this.groupRoleIds,
            active: true,
            updatedBy: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.editingGroupAccess ? 'Updated' : 'Saved',
                    detail: this.editingGroupAccess ? 'Group access updated.' : 'Group roles saved.'
                });
                this.resetGroupAccessForm();
                this.loadGroupUsers();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: err?.error?.message || 'Could not save group roles.' })
        });
    }

    editGroupAccess(access: CompanyGroupAccess): void {
        this.editingGroupAccess = access;
        this.groupCustomerId = access.customerId;
        this.groupRoleIds = (access.roles ?? [])
            .map(role => role.roleId)
            .filter((roleId): roleId is number => typeof roleId === 'number');
    }

    deleteGroupAccess(access: CompanyGroupAccess): void {
        if (!this.selectedGroupId || !access.customerId) {
            return;
        }
        const userName = this.accessUserName(access);
        if (!window.confirm(`Remove ${userName} from this company group?`)) {
            return;
        }
        this.tenantAccessService.deactivateCompanyGroupUser(
            this.selectedGroupId,
            access.customerId,
            this.sessionStorageService.getCustomer()?.id
        ).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Group access removed.' });
                if (this.editingGroupAccess?.customerId === access.customerId) {
                    this.resetGroupAccessForm();
                }
                this.loadGroupUsers();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Delete Failed', detail: err?.error?.message || 'Could not remove group access.' })
        });
    }

    resetGroupAccessForm(): void {
        this.editingGroupAccess = undefined;
        this.groupCustomerId = undefined;
        this.groupRoleIds = [];
    }

    saveCompanyAccess(): void {
        if (!this.canSaveCompanyAccess()) {
            return;
        }
        this.tenantAccessService.saveCompanyUser(this.childCompanyId!, {
            customerId: this.companyCustomerId!,
            roleIds: this.companyRoleIds,
            active: true,
            updatedBy: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Company roles saved.' });
                this.companyRoleIds = [];
                this.companyCustomerId = undefined;
                this.loadCompanyUsers();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: err?.error?.message || 'Could not save company roles.' })
        });
    }

    loadCompanyUsers(): void {
        if (!this.childCompanyId) {
            this.companyUsers = [];
            return;
        }
        this.tenantAccessService.getCompanyUsers(this.childCompanyId).subscribe(users => this.companyUsers = users ?? []);
    }

    accessUserName(access: CompanyAccess | CompanyGroupAccess): string {
        return access.customerName
            || this.customers.find(customer => customer.id === access.customerId)?.name
            || `Customer ${access.customerId ?? 'N/A'}`;
    }

    accessUserDetail(access: CompanyAccess | CompanyGroupAccess): string {
        return access.customerEmail
            || this.customers.find(customer => customer.id === access.customerId)?.emailAddress
            || `ID ${access.customerId ?? 'N/A'}`;
    }

    private loadGroups(): void {
        this.companyService.getCompanyGroups(true, true).subscribe(groups => {
            this.groups = groups ?? [];
            if (this.selectedGroupId && !this.groups.some(group => group.id === this.selectedGroupId)) {
                this.selectedGroupId = undefined;
                this.groupCompanies = [];
                this.groupUsers = [];
                this.invitations = [];
                this.membershipAuditEvents = [];
                this.resetGroupAccessForm();
            }
            if (!this.selectedGroupId && this.groups.length) {
                this.selectedGroupId = this.groups[0].id;
                this.onGroupSelected();
            } else if (this.selectedGroupId) {
                this.onGroupSelected();
            }
        });
    }

    private loadGroupDetails(): void {
        if (!this.selectedGroupId) {
            this.groupCompanies = [];
            this.childCompanyId = undefined;
            return;
        }
        forkJoin({
            companies: this.companyService.getCompanyGroupCompanies(this.selectedGroupId),
            owningCompanyIds: this.companyService.resolveCompanyGroupOwningCompanyIds(this.selectedGroupId)
        }).subscribe(({ companies, owningCompanyIds }) => {
            const registeredCompanyIds = new Set((owningCompanyIds ?? []).filter(id => typeof id === 'number'));
            this.groupCompanies = (companies ?? []).filter(company => registeredCompanyIds.has(company.id));
            this.childCompanyId = this.groupCompanies.find(company => company.id === this.company?.id)?.id
                ?? undefined;
        });
    }

    private loadGroupUsers(): void {
        if (!this.selectedGroupId) {
            this.groupUsers = [];
            this.resetGroupAccessForm();
            return;
        }
        this.tenantAccessService.getCompanyGroupUsers(this.selectedGroupId).subscribe(users => {
            this.groupUsers = users ?? [];
            if (this.editingGroupAccess?.customerId && !this.groupUsers.some(user => user.customerId === this.editingGroupAccess?.customerId)) {
                this.resetGroupAccessForm();
            }
        });
    }

    private loadInvitations(): void {
        if (!this.selectedGroupId) {
            this.invitations = [];
            return;
        }
        this.companyService.getCompanyGroupMembershipInvitations(this.selectedGroupId).subscribe({
            next: invitations => this.invitations = invitations ?? [],
            error: () => this.invitations = []
        });
    }

    private loadMembershipAuditEvents(): void {
        if (!this.selectedGroupId) {
            this.membershipAuditEvents = [];
            return;
        }
        this.companyService.getCompanyGroupMembershipAuditEvents(this.selectedGroupId).subscribe({
            next: events => this.membershipAuditEvents = events ?? [],
            error: () => this.membershipAuditEvents = []
        });
    }

    private get currentGroup(): CompanyGroup | undefined {
        return this.groups.find(group => group.id === this.selectedGroupId);
    }

    private loadCustomers(): void {
        if (!this.company?.id) {
            this.customers = [];
            return;
        }
        this.customerService.getCustomersByCompany(this.company.id).subscribe(customers => this.customers = customers ?? []);
    }

    private createGroupDraft(): Partial<CompanyGroup> {
        const baseName = this.company?.name ? `${this.company.name} Group` : '';
        return {
            name: baseName,
            code: this.toGroupCode(baseName),
            active: true,
            countryId: this.company?.countryId
        };
    }

    private toGroupCode(value: string): string {
        return value
            .trim()
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 40);
    }

    private isCompanyRole(role: Role): boolean {
        const scope = this.normalizedRoleScope(role);
        return (!scope || scope === 'COMPANY' || scope === 'LEGACY') && !this.hasPortalPermission(role);
    }

    private isCompanyGroupRole(role: Role): boolean {
        return this.normalizedRoleScope(role) === 'COMPANY_GROUP';
    }

    private normalizedRoleScope(role: Role): string {
        return (role.roleScope || '').trim().toUpperCase();
    }

    private hasPortalPermission(role: Role): boolean {
        return (role.permissions ?? []).some(permission =>
            (permission.permissionCode || '').trim().toLowerCase().startsWith('portal.')
        );
    }
}
